<template>
  <div class="left-col-bar border-e border-[#E0E0E0] border-solid min-w-[420px] w-[420px] p-4 h-[calc(100vh-108px)]">
    <!-- Sticky Header with Button and Progress Bar -->
    <div class="sticky-header bg-white rounded-t-xl p-5">
      <!-- Progress Bar with Button -->
      <div class="progressbar">
        <div class="flex justify-start mb-4">
          <a 
            href="https://calendly.com/askafundraiser-info/new-aaf-test-meeting" 
            target="_blank" 
            rel="noopener noreferrer"
            class="bg-[#3E02A0] text-white px-4 py-1 rounded-lg text-sm font-semibold hover:bg-[#320177] transition-colors duration-200 inline-block"
          >
            Book Consulting
          </a>
        </div>
        <div class="mb-2">
          <p class="text-[#666666] font-semibold text-[12px] leading-[1.2]">Your Fundraising Plan Progress</p>
        </div>
        <div class="w-full bg-[#EDEFEE] rounded-full">
          <div
            class="bg-[#00423D] text-xs font-regular text-[#ffffff] text-center p-0.5 leading-none rounded-full"
            :style="{ width: `${calculateTotalProgress()}%` }"
          >
            {{ calculateTotalProgress() }}%
          </div>
        </div>
      </div>
    </div>

    <div ref="sidebarRef" class="bg-white rounded-b-xl sidebar-content-wrapper overflow-y-auto p-5 pb-0 pt-0" style="height: calc(100vh - 260px)">

      <div class="progress-grid-wrapper">
        <template v-for="(module, index) in modules" :key="index">
          <div
            :ref="module.number === activeModule ? (el) => activeModuleRef = el : undefined"
            :class="[
              'border border-solid rounded-xl p-[0.75rem] relative mb-[1rem]',
              isModuleSkipped(module) ? 'opacity-50' : '',
              activeModule === module.number
                ? 'border-[#00423D] bg-[#EAE3D3] bg-opacity-25'
                : 'border-[#F0F0F0]',
            ]"
          >
            <div class="flex items-center justify-between w-full">
              <div class="flex items-center">
                <div class="min-w-[44px] w-[44px] h-[44px] min-h-[44px] mr-[12px] relative">
                  <svg class="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle 
                      :class="[
                        'stroke-current',
                        isModuleSkipped(module) ? 'text-[#D8D8D8]' : 'text-[#EBE8E1]'
                      ]" 
                      stroke-width="10" 
                      cx="50" 
                      cy="50" 
                      r="45" 
                      fill="transparent" 
                    />
                    <circle
                      v-if="!isModuleSkipped(module)"
                      class="text-[#00423D] stroke-current"
                      stroke-width="10"
                      stroke-linecap="round"
                      cx="50"
                      cy="50"
                      r="45"
                      fill="transparent"
                      stroke-dasharray="283"
                      :stroke-dashoffset="getStrokeOffset(module)"
                    />
                  </svg>
                  <span 
                    v-if="!isModuleSkipped(module)" 
                    class="absolute inset-0 flex items-center justify-center text-[12px] font-semibold text-[#414141]"
                  >
                    {{ calculateModuleProgress(module) }}%
                  </span>
                  <span 
                    v-else 
                    class="absolute inset-0 flex items-center justify-center"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 17C11.4696 17 10.9609 16.7893 10.5858 16.4142C10.2107 16.0391 10 15.5304 10 15C10 13.89 10.89 13 12 13C12.5304 13 13.0391 13.2107 13.4142 13.5858C13.7893 13.9609 14 14.4696 14 15C14 15.5304 13.7893 16.0391 13.4142 16.4142C13.0391 16.7893 12.5304 17 12 17ZM18 20V10H6V20H18ZM18 8C18.5304 8 19.0391 8.21071 19.4142 8.58579C19.7893 8.96086 20 9.46957 20 10V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V10C4 9.46957 4.21071 8.96086 4.58579 8.58579C4.96086 8.21071 5.46957 8 6 8H7V6C7 4.67392 7.52678 3.40215 8.46447 2.46447C9.40215 1.52678 10.6739 1 12 1C12.6566 1 13.3068 1.12933 13.9134 1.3806C14.52 1.63188 15.0712 2.00017 15.5355 2.46447C15.9998 2.92876 16.3681 3.47995 16.6194 4.08658C16.8707 4.69321 17 5.34339 17 6V8H18ZM12 3C11.2044 3 10.4413 3.31607 9.87868 3.87868C9.31607 4.44129 9 5.20435 9 6V8H15V6C15 5.20435 14.6839 4.44129 14.1213 3.87868C13.5587 3.31607 12.7956 3 12 3Z" fill="#666666"/>
                    </svg>
                  </span>
                </div>
                <div class="flex flex-col">
                  <span 
                    class="text-[#666666] text-[12px] leading-[1.25rem] mb-[2px] font-semibold cursor-pointer inline w-fit" 
                    @click="toggleModule(module.number)"
                  >Module {{ module.number }}</span>
                  <span 
                    class="font-minion text-[#00423D] text-[18px] leading-[22px] font-semibold cursor-pointer inline w-fit" 
                    @click="toggleModule(module.number)"
                  >{{ module.title }}</span>
                </div>
              </div>
              <div 
                class="cursor-pointer" 
                v-if="module.sections.length > 0"
                @click="toggleModule(module.number)"
              >
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  :class="{ 'rotate-180': activeModule !== module.number }"
                  class="transform transition-transform duration-300"
                >
                  <path d="M9.99967 9.30412L4.8038 14.5L3.40173 13.0979L9.99967 6.5L16.5976 13.0979L15.1955 14.5L9.99967 9.30412Z" fill="#00423D" />
                </svg>
              </div>
            </div>

            <div v-if="activeModule === module.number && module.sections.length > 0" class="progress-grid-content pt-[20px] pl-[0.75rem]">
              <ol class="relative text-[#414141] text-[12px] font-semibold">
                <li v-for="(section, sIndex) in module.sections" :key="sIndex" class="pb-[12px] flex items-center relative">
                  <div
                    v-if="sIndex !== module.sections.length - 1"
                    :class="[
                      'h-full w-[1px] absolute left-[9px] top-[12px]',
                      routesManagement.routeState[getSectionPath(module.number, sIndex)]?.status === 1 ? 'bg-[#00423D]' : 'bg-[#EBE8E1]',
                    ]"
                  ></div>
                  <span
                    class="flex items-center justify-center w-5 h-5 rounded-full border-2 bg-[#faf8f4] z-[2] relative"
                    :class="[
                      routesManagement.routeState[getSectionPath(module.number, sIndex)]?.status === 1 ? 'border-[#00423D] cursor-pointer' : 
                      'border-[#EBE8E1] cursor-pointer'
                    ]"
                    @click.stop="navigateToSection(module.number, sIndex, section)"
                  >
                    <svg v-if="routesManagement.routeState[getSectionPath(module.number, sIndex)]?.status === 1" width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.6 8.59999L11.65 1.54999L10.25 0.149994L4.6 5.79999L1.75 2.94999L0.349998 4.34999L4.6 8.59999Z" fill="#00423D" />
                    </svg>
                    <svg v-else-if="routesManagement.routeState[getSectionPath(module.number, sIndex)]?.status === 0 && route.path === getSectionPath(module.number, sIndex)" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0029 0C5.38682 0 0 5.38683 0 12.0029C0 18.619 5.38682 24 12.0029 24C18.619 24 24 18.619 24 12.0029C24 5.38683 18.619 0 12.0029 0ZM12.0029 2.00016C17.5381 2.00016 21.9998 6.4678 21.9998 12.0029C21.9998 17.5381 17.5381 21.9998 12.0029 21.9998C6.46779 21.9998 2.00016 17.5381 2.00016 12.0029C2.00016 6.4678 6.46779 2.00016 12.0029 2.00016Z" fill="#00423D"/>
                    </svg>
                  </span>
                  <h3
                    class="pl-[1.5rem] cursor-pointer hover:text-[#00423D]"
                    :class="[
                      { 'pointer-events-none opacity-50': isModuleSkipped(module) && !(module.number === 9 && section.title === 'Overview') }
                    ]"
                    @click.stop="navigateToSection(module.number, sIndex, section)"
                  >{{ section.title }}</h3>
                </li>
              </ol>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useRoutesManagement } from "@/stores/routesManagement";
import { useSkippedModulesStore } from "@/stores/skippedModules";
import { useUserProjectsStore } from "@/stores/projects";

const router = useRouter();
const route = useRoute();
const activeModule = ref(1);
const activeModuleRef = ref(null);
const sidebarRef = ref(null);
const routesManagement = useRoutesManagement();
const skippedModulesStore = useSkippedModulesStore();
const projectsStore = useUserProjectsStore();
const modules = ref([
  {
    number: 1,
    title: "Getting Started",
    sections: [
      { title: "Welcome" },
      // { title: "Fundraising & Sustainability Self-Assessment" },
      { title: "Collaboration & Adding Team Members" },
      { title: "Fundraising Resources Overview" },
      { title: "Understanding Your Case for Support" },
      { title: "Understanding Your Stewardship Structure" },
    ],
  },
  {
    number: 2,
    title: "Strategic Planning for Fundraising Overview",
    sections: [
      { title: "Overview" }
    ],
  },
  {
    number: 3,
    title: "Vision & Mission",
    sections: [
      { title: "Overview" },
      { title: "Societal Challenge and Organizational Solution" },
      { title: "Societal / Community Impact" },
      { title: "Aspirational Vision" },
      { title: "Mission Statement" },
    ],
  },
  {
    number: 4,
    title: "Core Annual Programs – Goals and Rationale",
    sections: [
      { title: "Overview" },
      { title: "Naming Your Core Annual Programs" },
    ],
  },
  {
    number: 5,
    title: "Annual Operating Budget",
    sections: [
      { title: "Overview" },
      { title: "Annual Overhead Expenses" },
      { title: "Budget Justification" }
    ],
  },
  {
    number: 6,
    title: "Annual Revenue",
    sections: [
      { title: "Annual Revenue" }
    ],
  },
  {
    number: 7,
    title: "Fundraising Goal Overview",
    sections: [
      { title: "Year 1 Annual Operating Budget Summary" },
      { title: "Annual Fundraising Goals (3 Years)" }
    ],
  },
  {
    number: 8,
    title: "Evaluation: Outcomes and Outputs",
    sections: [
      { title: "Overview" },
    ],
  },
  {
    number: 9,
    title: "New Projects",
    sections: [
      { title: "Overview" },
      { title: "Enter New Projects" },
      // Dynamic sections will be added here for each new project
      // { title: "Project Name", projectId: "id", projectSlug: "slug", slug: "slug" }
      { title: "New Project(s) Fundraising Goal (3 Years)" }
    ],
  },
  {
    number: 10,
    title: "3-Year Fundraising Goal Overview",
    sections: [
      { title: "Summary" }
    ],
  },
  {
    number: 11,
    title: "Case for Support",
    sections: [
      { title: "Overview" },
      { title: "Letter From the Executive Director" },
      { title: "Our Story" },
      { title: "Call for More" },
      { title: "Annual Programs Summary" },
      { title: "Join Us" },
      { title: "Ways to Give" },
      { title: "Impact Statement" },
      { title: "Success Story Highlights" },
      { title: "Fundraising Talking Points" },
      { title: "Case for Support", path: "/case-statement-document" },
    ],
  },
  {
    number: 12,
    title: "Gift Pyramid",
    sections: [
      { title: "Overview" },
      { title: "Donor: Prospect Ratio & Gift Pyramid"}
    ],
  },
  {
    number: 13,
    title: "Stewardship Structure",
    sections: [
      { title: "Overview" },
      { title: "Donor Recognition and Benefit Structure" }
    ]
  },
  {
    number: 14,
    title: "Policies & Procedures Manual",
    sections: [
      { title: "Overview" },
      { title: "Policies and Procedures" },
      { title: "Development Documents" }
    ]
  },
  {
    number: 15,
    title: "Congratulations, Strategic Plan for Fundraising Completed",
    sections: [
      { title: "Certificate" }
    ]
  },
]);

onMounted(async () => {  
  try {
    // Initialize projects store with all necessary data first
    await projectsStore.initializeWithAllData();
    
    // Update modules with dynamic sections before populating routes
    if (projectsStore.corePrograms && Array.isArray(projectsStore.corePrograms)) {
      // Module 4 (previously Module 5)
      const module4 = modules.value.find(m => m.number === 4);
      if (module4) {
        const baseSections = module4.sections.slice(0, 2);
        const dynamicSections = projectsStore.corePrograms.map((program, index) => ({
          title: `${index + 1} - ${program.program_name}`,
          programId: program.id,
          programSlug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        }));
        module4.sections = [...baseSections, ...dynamicSections];
      }
      
      // Module 8 (previously Module 9)
      const module8 = modules.value.find(m => m.number === 8);
      if (module8) {
        const baseSections = module8.sections.slice(0, 1);
        const dynamicSections = projectsStore.corePrograms.map((program, index) => ({
          title: `${index + 1} - ${program.program_name}`,
          programId: program.id,
          programSlug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        }));
        module8.sections = [...baseSections, ...dynamicSections];
      }
    }
    
    // Get new projects from the store (they should already be fetched by now)
    let newProjects = [];
    try {
      newProjects = await projectsStore.getNewProjects();
    } catch (error) {
      console.warn('Failed to fetch new projects, using empty array:', error);
      newProjects = [];
    }
    
    if (newProjects && Array.isArray(newProjects)) {
      const module9 = modules.value.find(m => m.number === 9);
      if (module9) {
        // Build sections directly without duplication
        const baseSections = [
          { title: "Overview" },
          { title: "Enter New Projects" }
        ];
        const dynamicSections = newProjects.map((project, index) => ({
          title: `${index + 1} - ${project.project_name}`,
          projectId: project.id,
          projectSlug: project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          slug: project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        }));
        const remainingSections = [
          { title: "New Project(s) Fundraising Goal (3 Years)" }
        ];
        module9.sections = [...baseSections, ...dynamicSections, ...remainingSections];
        
        // Force a re-render
        nextTick(() => { activeModule.value = activeModule.value; });
      }
    }
    
    // Now populate routes after dynamic sections are added
    await routesManagement.populateRouteState(modules.value, true);
    
    // Force a fresh fetch of skipped modules data
    await skippedModulesStore.fetchSkippedModules(true);
    
    // Check skipped modules state after loading
    if (!skippedModulesStore.initialized || skippedModulesStore.skippedRoutes === null) {
      // Short delay before retrying
      setTimeout(async () => {
        await skippedModulesStore.fetchSkippedModules(true);
        nextTick(() => { activeModule.value = activeModule.value; });
      }, 500);
    }
    
    // Add event listeners
    window.addEventListener('skipped-modules-updated', async () => {
      await skippedModulesStore.fetchSkippedModules(true);
      nextTick(() => { activeModule.value = activeModule.value; });
    });
    
    window.addEventListener('core-programs-updated', async (event) => {
      // Store current route states before updating
      const currentRouteState = { ...routesManagement.routeState };
      
      // Module 4 dynamic sections (previously Module 5)
      const module4 = modules.value.find(m => m.number === 4);
      if (module4) {
        const baseSections = module4.sections.slice(0, 2);
        const dynamicSections = event.detail.programs.map((program, index) => ({
          title: `${index + 1} - ${program.program_name}`,
          programId: program.id,
          programSlug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        }));
        module4.sections = [...baseSections, ...dynamicSections];
      }
      
      // Module 8 dynamic sections (previously Module 9)
      const module8 = modules.value.find(m => m.number === 8);
      if (module8) {
        const baseSections = module8.sections.slice(0, 1);
        const dynamicSections = event.detail.programs.map((program, index) => ({
          title: `${index + 1} - ${program.program_name}`,
          programId: program.id,
          programSlug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        }));
        module8.sections = [...baseSections, ...dynamicSections];
      }
      
      // Add a delay to avoid overwriting recently completed routes
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        // Re-populate route state to include dynamic routes, preserving existing completed states
        await routesManagement.populateRouteState(modules.value, true);
      } catch (error) {
        console.error('Failed to update routes after core programs update:', error);
        // Restore previous route states on error
        routesManagement.routeState = currentRouteState;
      }
      
      nextTick(() => { activeModule.value = activeModule.value; });
    });
    
    // Add event listener for new projects updates
    window.addEventListener('new-projects-updated', async (event) => {
      // Store current route states before updating
      const currentRouteState = { ...routesManagement.routeState };
      
      // Module 9 dynamic sections for new projects
      const module9 = modules.value.find(m => m.number === 9);
      if (module9) {
        // Build sections directly without duplication
        const baseSections = [
          { title: "Overview" },
          { title: "Enter New Projects" }
        ];
        const dynamicSections = event.detail.projects.map((project, index) => ({
          title: `${index + 1} - ${project.project_name}`,
          projectId: project.id,
          projectSlug: project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          slug: project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        }));
        const remainingSections = [
          { title: "New Project(s) Fundraising Goal (3 Years)" }
        ];
        module9.sections = [...baseSections, ...dynamicSections, ...remainingSections];
      }
      
      // Add a delay to avoid overwriting recently completed routes
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        // Re-populate route state to include dynamic routes, preserving existing completed states
        await routesManagement.populateRouteState(modules.value, true);
      } catch (error) {
        console.error('Failed to update routes after new projects update:', error);
        // Restore previous route states on error
        routesManagement.routeState = currentRouteState;
      }
      
      nextTick(() => { activeModule.value = activeModule.value; });
    });
    
    await nextTick();
  } catch (error) {
    console.error("Error initializing StepsNav component:", error);
  }
});

onUnmounted(() => {
  // Remove event listener when component is unmounted
  window.removeEventListener('core-programs-updated', () => {});
  window.removeEventListener('new-projects-updated', () => {});
});

const getSectionPath = (moduleNumber, sectionIndex) => {
  // Special case for module 11, section 10 (Case for Support)
  if (moduleNumber === 11 && sectionIndex === 10) {
    return '/case-statement-document';
  }
  
  // Use the RoutesManagement method for all path generation, passing modules for dynamic sections
  return routesManagement.getRoutePath(moduleNumber, sectionIndex, modules.value);
};

const navigateToSection = (moduleNumber, sectionIndex, section) => {
  // Ensure skipped modules data is loaded
  if (!skippedModulesStore.initialized) {
    skippedModulesStore.fetchSkippedModules();
    return;
  }
  
  // Check if the module is skipped using the isModuleSkipped function for consistency
  const isSkipped = isModuleSkipped({ number: moduleNumber });
  
  // Special case: Allow navigation to Module 9 overview page even when skipped
  const isModule9Overview = moduleNumber === 9 && section.title === "Overview";
  
  // Special handling for Module 4 (previously Module 5)
  if (moduleNumber === 4) {
    const module4 = modules.value.find(m => m.number === 4);
    if (module4) {
      // If this is a dynamic program section
      if (section.programId) {
        // Use the existing slug property instead of recreating it from title
        const dynamicPath = `/steps/funding-annual/program/${section.slug}`;
        
        // Allow navigation if module is not skipped
        if (!isSkipped) {
          router.push(dynamicPath);
          return;
        }
      } else if (sectionIndex < 2) {
        // Handle base sections
        const basePaths = [
          '/steps/funding-annual/overview',
          '/steps/funding-annual/core-programs'
        ];
        if (!isSkipped) {
          router.push(basePaths[sectionIndex]);
          return;
        }
      } else {
        // No need to handle remaining sections anymore
        return;
      }
    }
  }
  
  // Special handling for Module 8 (previously Module 9)
  if (moduleNumber === 8) {
    const module8 = modules.value.find(m => m.number === 8);
    if (module8) {
      if (section.programId) {
        // Use the existing slug property instead of recreating it from title
        const dynamicPath = `/steps/core-program-evaluation/program/${section.slug}`;
        if (!isSkipped) {
          router.push(dynamicPath);
          return;
        }
      } else if (sectionIndex === 0) {
        if (!isSkipped) {
          router.push('/steps/core-program-evaluation/overview');
          return;
        }
      } else {
        return;
      }
    }
  }
  
  // Special handling for Module 9 dynamic project sections
  if (moduleNumber === 9) {
    const module9 = modules.value.find(m => m.number === 9);
    if (module9) {
      if (section.projectId) {
        // Use the existing slug property instead of recreating it from title
        const dynamicPath = `/steps/funding-new-projects/project/${section.slug}`;
        if (!isSkipped) {
          router.push(dynamicPath);
          return;
        }
      } else if (sectionIndex < 2) {
        // Handle base sections (Overview and Enter New Projects)
        const basePaths = [
          '/steps/funding-new-projects/overview',
          '/steps/funding-new-projects/new-projects'
        ];
        if (!isSkipped) {
          router.push(basePaths[sectionIndex]);
          return;
        }
      } else if (sectionIndex >= module9.sections.length - 1) {
        // Handle remaining sections (New Project Fundraising Goals)
        const remainingPaths = [
  
          '/steps/funding-new-projects/new-project-fundraising-goals'
        ];
        const remainingIndex = sectionIndex - (module9.sections.length - 1);
        if (!isSkipped) {
          router.push(remainingPaths[remainingIndex]);
          return;
        }
      } else {
        return;
      }
    }
  }
  
  // For all other modules, use the standard path
  const path = getSectionPath(moduleNumber, sectionIndex);
  
  // Allow navigation if:
  // 1. The module is not skipped, OR
  // 2. It's the overview of Module 9
  if (!isSkipped || isModule9Overview) {
    router.push(path);
  }
};

const getStrokeOffset = (module) => {
  const progress = calculateModuleProgress(module);
  return 283 - (283 * progress) / 100;
};

const toggleModule = (moduleNumber) => {
  if (activeModule.value === moduleNumber) {
    activeModule.value = null;
  } else {
    activeModule.value = moduleNumber;
  }
};

const isModuleSkipped = (module) => {
  // If skipped modules store is not initialized or data not loaded yet, return false
  if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
    return false;
  }
  
  // Get the path for this module (just the module path, not including section)
  const modulePath = `/steps/${getModulePathFromNumber(module.number)}`;
  
  // Check for module in skipped routes
  const isSkipped = skippedModulesStore.skippedRoutes.includes(modulePath);
  
  // Special case: For module 9, we want it to appear skipped in the UI
  // but still allow access to the overview
  if (module.number === 9 && isSkipped) {
    return true;
  }
  
  return isSkipped;
};

const getModulePathFromNumber = (moduleNumber) => {
  const moduleNames = {
    1: "getting-started",
    2: "strategic-planning",
    3: "aspirational-vision",
    4: "funding-annual",
    5: "finalizing-budget",
    6: "annual-revenue",
    7: "annual-operating-budget",
    8: "core-program-evaluation",
    9: "funding-new-projects",
    10: "total-fundraising-goal",
    11: "finalize-your-case-for-support",
    12: "gift-pyramid",
    13: "stewardship",
    14: "fundraising-operations",
    15: "congratulations"
  };
  return moduleNames[moduleNumber];
};

const calculateModuleProgress = (module) => {
  // Return 0 progress for skipped modules
  if (isModuleSkipped(module)) return 0;
  
  // For non-skipped modules, calculate actual progress
  if (!module.sections.length) return 0;
  
  let completedSections = 0;
  let totalSections = 0;
  
  module.sections.forEach((section, index) => {
    const path = getSectionPath(module.number, index);
    // Only count sections that have a valid path and are not skipped
    if (path && routesManagement.routeState[path]) {
      totalSections++;
      if (routesManagement.routeState[path]?.status === 1) {
        completedSections++;
      }
    }
  });
  
  return totalSections > 0 ? Math.round((completedSections / totalSections) * 100) : 0;
};

const calculateTotalProgress = () => {
  let totalSections = 0;
  let totalCompleted = 0;
  
  modules.value.forEach((module) => {
    if (!isModuleSkipped(module)) {
      module.sections.forEach((section, index) => {
        const path = getSectionPath(module.number, index);
        // Only count sections that have a valid path and are not skipped
        if (path && routesManagement.routeState[path]) {
          totalSections++;
          if (routesManagement.routeState[path]?.status === 1) {
            totalCompleted++;
          }
        }
      });
    }
  });
  
  return totalSections > 0 ? Math.round((totalCompleted / totalSections) * 100) : 0;
};

const scrollToActiveModule = async () => {
  await nextTick();
  if (activeModuleRef.value && sidebarRef.value) {
    const container = sidebarRef.value;
    const element = activeModuleRef.value;
    
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    // Calculate if element is in view
    const isInView = (
      elementRect.top >= containerRect.top &&
      elementRect.bottom <= containerRect.bottom
    );
    
    if (!isInView) {
      container.scrollTo({
        top: element.offsetTop - container.offsetTop - 20,
        behavior: 'smooth'
      });
    }
  }
};

watch(activeModule, () => {
  scrollToActiveModule();
}, { immediate: true });

// Add a method to check if data is stale and needs refreshing
const isDataStale = () => {
  // Check if core programs are stale (older than 5 minutes)
  const coreProgramsTimestamp = localStorage.getItem('coreProgramsTimestamp');
  if (coreProgramsTimestamp) {
    const age = Date.now() - parseInt(coreProgramsTimestamp);
    if (age > 5 * 60 * 1000) { // 5 minutes
      return true;
    }
  }
  
  // Check if new projects are stale (older than 5 minutes)
  const newProjectsTimestamp = localStorage.getItem('newProjectsTimestamp');
  if (newProjectsTimestamp) {
    const age = Date.now() - parseInt(newProjectsTimestamp);
    if (age > 5 * 60 * 1000) { // 5 minutes
      return true;
    }
  }
  
  return false;
};

// Add a method to refresh data if needed
const refreshDataIfNeeded = async () => {
  try {
    // Check if data is stale
    if (isDataStale()) {
      
      // Refresh core programs
      if (!projectsStore.corePrograms.length) {
        await projectsStore.fetchCorePrograms();
      }
      
      // Refresh new projects
      const newProjects = projectsStore.syncNewProjectsFromStorage();
      if (!newProjects.length) {
        await projectsStore.fetchNewProjects();
      }
    }
  } catch (error) {
    console.warn('Failed to refresh data:', error);
  }
};

// Add watcher for route changes to refresh data if needed
watch(() => route.path, async (newPath) => {
  // Find the module whose section matches the current path
  let foundModuleNumber = 1;
  modules.value.forEach(module => {
    module.sections.forEach((_, index) => {
      if (getSectionPath(module.number, index) === newPath) {
        foundModuleNumber = module.number;
      }
    });
  });
  activeModule.value = foundModuleNumber;
  scrollToActiveModule();
  
  // Refresh data if we're navigating to a module that needs dynamic data
  if (foundModuleNumber === 4 || foundModuleNumber === 8 || foundModuleNumber === 9) {
    await refreshDataIfNeeded();
  }
}, { immediate: true });

// Add watcher for skipped routes
watch(
  () => skippedModulesStore.skippedRoutes,
  (newSkippedRoutes) => {
    // Force re-rendering by triggering the reactive refs
    activeModule.value = activeModule.value;
  },
  { deep: true }
);

// Add watcher for route state changes to ensure UI updates immediately
watch(
  () => routesManagement.routeState,
  (newRouteState) => {
    // Force re-rendering when route state changes
    activeModule.value = activeModule.value;
  },
  { deep: true }
);

// Update the watcher for core programs
watch(() => projectsStore.corePrograms, async (newCorePrograms, oldCorePrograms) => {
  if (newCorePrograms && Array.isArray(newCorePrograms)) {
    // Check if this is a reordering (same programs, different order) or deletion/addition
    const isReordering = oldCorePrograms && 
      oldCorePrograms.length === newCorePrograms.length &&
      oldCorePrograms.every(oldProg => 
        newCorePrograms.some(newProg => newProg.id === oldProg.id)
      );
    
    // If it's reordering, preserve route status
    if (isReordering && oldCorePrograms) {
      routesManagement.preserveRouteStatus(oldCorePrograms, newCorePrograms);
    }
    
    // Module 4 (previously Module 5)
    const module4 = modules.value.find(m => m.number === 4);
    if (module4) {
      const baseSections = module4.sections.slice(0, 2);
      const dynamicSections = newCorePrograms.map((program, index) => ({
        title: `${index + 1} - ${program.program_name}`,
        programId: program.id,
        programSlug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      }));
      module4.sections = [...baseSections, ...dynamicSections];
      nextTick(() => { activeModule.value = activeModule.value; });
    }
    // Module 8 (previously Module 9)
    const module8 = modules.value.find(m => m.number === 8);
    if (module8) {
      const baseSections = module8.sections.slice(0, 1);
      const dynamicSections = newCorePrograms.map((program, index) => ({
        title: `${index + 1} - ${program.program_name}`,
        programId: program.id,
        programSlug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      }));
      module8.sections = [...baseSections, ...dynamicSections];
      nextTick(() => { activeModule.value = activeModule.value; });
    }
    
    // Re-populate route state to include dynamic routes
    await routesManagement.populateRouteState(modules.value, true);
  }
}, { immediate: true, deep: true });

// Add watcher for new projects from localStorage
// Removed localStorage watcher for new projects to prevent duplicates
// Now only using event-based updates via 'new-projects-updated' event

</script>

<style scoped>
.sidebar-content-wrapper::-webkit-scrollbar,
.right-content-grid-vs::-webkit-scrollbar {
  width: 8px;
}

/* Track */
.sidebar-content-wrapper::-webkit-scrollbar-track,
.right-content-grid-vs::-webkit-scrollbar-track {
  background: #EBE8E1;
}

/* Handle */
.sidebar-content-wrapper::-webkit-scrollbar-thumb,
.right-content-grid-vs::-webkit-scrollbar-thumb {
  background: #00423D;
  border-radius: 6px;
}
</style>
